import React, { Dispatch, SetStateAction } from "react"

import { Form, Formik } from "formik"

import { SelectOption } from "~tailwindui/types/SelectOption"

import { AutocompleteSelect } from "~tailwindui/Form"

import classNames from "src/classNames"

import Avatar from "components/Messaging/Avatar"

import NewThreadModal from "./NewThreadModal"
import UnreadBadge from "./UnreadBadge"

import { MessageThread } from "."

export type ThreadListProps = {
  viewer: {
    id: string
    messagingDisplayName: string
    avatar: { thumbUrl: string }
    messageThreads: {
      data: MessageThread[]
    }
  }
  openThread:
    | {
        id: string
      }
    | undefined
  setOpenThread: Dispatch<SetStateAction<MessageThread | null>>
  userFilter: SelectOption | undefined
  setUserFilter: Dispatch<SetStateAction<SelectOption | undefined>>
  refreshQuery: VoidFunction
}

const ThreadList: React.FC<ThreadListProps> = ({
  viewer,
  openThread,
  setOpenThread,
  userFilter,
  setUserFilter,
  refreshQuery,
}) => {
  const firstThreadUser = (thread: MessageThread) =>
    thread.users.find(u => u.id !== viewer.id) ?? thread.author

  return (
    <div className="divide-y divide-gray-200">
      <div className="border-b px-2 pt-2 pb-4">
        <NewThreadModal
          updateThreads={newThread => {
            refreshQuery()
            setOpenThread(newThread)
          }}
        />
      </div>
      <div className="p-2">
        {(viewer.messageThreads.data.length > 3 || userFilter) && (
          <div className="mb-4">
            <Formik initialValues={{ userId: "" }} onSubmit={() => undefined}>
              <Form>
                <AutocompleteSelect
                  label="Filter By User"
                  name="user"
                  resource="threadParticipants"
                  searchColumn="fullName"
                  clearable
                  defaultValue={userFilter}
                  afterChange={opt => {
                    setUserFilter(opt as SelectOption)
                  }}
                />
              </Form>
            </Formik>
          </div>
        )}
        <div className="divide-y divide-gray-200">
          {viewer.messageThreads.data.length === 0 && (
            <div className="py-4 italic md:hidden">No threads to display.</div>
          )}
          {viewer.messageThreads.data.map(thread => {
            const other = firstThreadUser(thread)
            const last = thread.messages.data[0]
            const subtitle = last
              ? `${last.author.id === viewer.id ? "You: " : ""}${last.content}`
              : undefined
            const selected = openThread?.id === thread.id
            return (
              <div
                key={thread.id}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === "Enter" && setOpenThread(thread)}
                onClick={() => setOpenThread(thread)}
                className={classNames(
                  "relative flex cursor-pointer py-3 transition-colors hover:bg-gray-50 md:pl-4",
                  selected && "bg-gray-100"
                )}
              >
                {selected && (
                  <div
                    className="absolute left-0 top-0 bottom-0 w-1 bg-brand-600"
                    aria-hidden
                  />
                )}
                <div className="my-auto flex flex-shrink-0 select-none rounded-full border border-gray-300 p-0.5">
                  <Avatar
                    src={other.avatar.thumbUrl}
                    alt={other.messagingDisplayName}
                    size="small"
                  />
                </div>
                <div className="my-auto ml-5 flex flex-1 flex-col overflow-hidden">
                  <div className="truncate text-lg font-medium">
                    {thread.title}
                  </div>
                  {subtitle != null && (
                    <div className="truncate text-sm text-gray-500">
                      {subtitle}
                    </div>
                  )}
                </div>
                <div className="my-auto ml-auto mr-4">
                  <UnreadBadge count={thread.unreadMessagesCount} />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ThreadList
